/**
 * Standardized RxAssistant patient context.
 *
 * A single, page-independent context package attached to every RxAssistant
 * conversation so the assistant reasons from the same baseline understanding of
 * the patient regardless of which page the clinician launched it from. Replaces
 * the old per-page metrics blob (which omitted demographics, motivations,
 * dietary restrictions, and CGM).
 *
 * This module is PURE — no React, no query hooks, no `Date.now()` baked in — so
 * it's unit-testable in isolation. The caller (a hook) resolves the raw data
 * from the React Query cache and hands it in as `PatientContextSources`;
 * `buildPatientContext` shapes it into the canonical `PatientContext`, and
 * `formatPatientContext` renders the string that gets embedded in the prompt /
 * copied to the clipboard.
 *
 * Design note: "RxAssistant Patient Context" (Phase 1 = client-assembled).
 * Fields with no API source yet (allergies, conditions, food preferences) are
 * listed in `_unavailable` so the model knows they're absent rather than
 * inferring them — Phase 2 (server-owned context) fills them in.
 */

import { formatPatientTags, formatDietRestrictions, type NamedTag } from '@/pages/patients/components/patientTags';

/** Attributes the standardized context cannot supply from the portal today.
 *  Surfaced to the model so it doesn't invent them. Removed as MNT adds the
 *  backing fields (Phase 2). */
export const PATIENT_CONTEXT_UNAVAILABLE = [
  'allergies',
  'conditions',
  'food_preferences',
] as const;

/** Raw inputs the assembler needs. Every field is optional — a section is
 *  simply omitted from the output when its source isn't loaded, so the context
 *  degrades gracefully rather than blocking or faking data. */
export type PatientContextSources = {
  patientId: number;
  patient?: {
    birthday?: string | null;
    sex?: string | null;
    motivations?: NamedTag[] | null;
    diet_restrictions?: NamedTag[] | null;
  } | null;
  clinics?: Array<{ name: string }> | null;
  /** Header "details" dates: tracking/subscription start, next & last report. */
  profile?: {
    startDate?: string | null;
    nextReportDate?: string | null;
    lastReportDate?: string | null;
  } | null;
  goals?: Array<{ name: string; target?: string | null; progress?: string | null }> | null;
  dateRange?: { from: string; to: string } | null;
  mealLogging?: {
    mealsLogged?: number | null;
    daysWithLogs?: number | null;
    avgFirstMeal?: string | null;
    avgLastMeal?: string | null;
    mealsPerDay?: number | null;
  } | null;
  dietQuality?: { medScore?: number | null; subscores?: Record<string, number> | null } | null;
  averagePlate?: Record<string, number> | null;
  /** Average calories per logged day. */
  caloriesPerDay?: number | null;
  macronutrients?: {
    carbs?: number | null;
    fat?: number | null;
    protein?: number | null;
    fiber?: number | null;
  } | null;
  cgm?: { timeInRangePct?: number | null; stats?: Record<string, number> | null } | null;
  /** Injectable "today" for deterministic age math in tests. */
  now?: Date;
};

export type PatientContext = {
  core: {
    patient: { id: number; age: number | null; sex: string | null };
    motivations: string[];
    dietary_restrictions: string[];
    clinics: string[];
    /** Mirrors the header "details" row (dates only; email is intentionally
     *  omitted as PII with little analytical value). Present when known. */
    profile?: { start_date?: string; next_report_date?: string; last_report_date?: string };
    active_goals: Array<{ name: string; target?: string; progress?: string }>;
  };
  metrics: {
    date_range?: { from: string; to: string };
    meal_logging?: {
      meals_logged?: number;
      days_with_logs?: number;
      avg_first_meal?: string;
      avg_last_meal?: string;
      meals_per_day?: number;
    };
    diet_quality?: { med_score?: number; subscores?: Record<string, number> };
    average_plate?: Record<string, number>;
    calories_per_day?: number;
    macronutrients?: { carbs?: number; fat?: number; protein?: number; fiber?: number };
    cgm?: { time_in_range_pct?: number; stats?: Record<string, number> };
  };
  /** Attributes not available from the portal (see PATIENT_CONTEXT_UNAVAILABLE). */
  _unavailable: string[];
};

/** Whole years between `birthday` (YYYY-MM-DD) and `now`. Null when the date is
 *  missing or unparseable. */
export const ageFromBirthday = (
  birthday: string | null | undefined,
  now: Date,
): number | null => {
  if (!birthday) return null;
  const m = birthday.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  let age = now.getFullYear() - year;
  const monthDiff = now.getMonth() + 1 - month;
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < day)) age -= 1;
  return age >= 0 && age < 150 ? age : null;
};

/** Drop null/undefined values from an object; return undefined if nothing is
 *  left, so empty sections are omitted from the context entirely. The result
 *  type strips `null` from the values (they've been filtered out at runtime). */
const compact = <T extends Record<string, unknown>>(
  obj: T,
): { [K in keyof T]?: NonNullable<T[K]> } | undefined => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined) out[k] = v;
  }
  return Object.keys(out).length > 0
    ? (out as { [K in keyof T]?: NonNullable<T[K]> })
    : undefined;
};

/**
 * Assemble the canonical `PatientContext` from resolved data. Missing sections
 * are omitted (not zero-filled), so the assistant never sees fabricated values.
 */
export function buildPatientContext(sources: PatientContextSources): PatientContext {
  const now = sources.now ?? new Date();
  const p = sources.patient ?? undefined;

  const context: PatientContext = {
    core: {
      patient: {
        id: sources.patientId,
        age: ageFromBirthday(p?.birthday, now),
        sex: p?.sex ?? null,
      },
      motivations: formatPatientTags(p?.motivations ?? undefined),
      dietary_restrictions: formatDietRestrictions(p?.diet_restrictions ?? undefined),
      clinics: (sources.clinics ?? []).map((c) => c.name).filter(Boolean),
      active_goals: (sources.goals ?? [])
        .map((g) => {
          const goal: { name: string; target?: string; progress?: string } = { name: g.name };
          if (g.target != null) goal.target = g.target;
          if (g.progress != null) goal.progress = g.progress;
          return goal;
        })
        .filter((g) => g.name),
    },
    metrics: {},
    _unavailable: [...PATIENT_CONTEXT_UNAVAILABLE],
  };

  const profile = sources.profile
    ? compact({
        start_date: sources.profile.startDate,
        next_report_date: sources.profile.nextReportDate,
        last_report_date: sources.profile.lastReportDate,
      })
    : undefined;
  if (profile) context.core.profile = profile;

  if (sources.dateRange) context.metrics.date_range = sources.dateRange;

  const mealLogging = sources.mealLogging
    ? compact({
        meals_logged: sources.mealLogging.mealsLogged,
        days_with_logs: sources.mealLogging.daysWithLogs,
        avg_first_meal: sources.mealLogging.avgFirstMeal,
        avg_last_meal: sources.mealLogging.avgLastMeal,
        meals_per_day: sources.mealLogging.mealsPerDay,
      })
    : undefined;
  if (mealLogging) context.metrics.meal_logging = mealLogging;

  const dietQuality = sources.dietQuality
    ? compact({
        med_score: sources.dietQuality.medScore,
        subscores: sources.dietQuality.subscores,
      })
    : undefined;
  if (dietQuality) context.metrics.diet_quality = dietQuality;

  if (sources.averagePlate && Object.keys(sources.averagePlate).length > 0) {
    context.metrics.average_plate = sources.averagePlate;
  }

  if (sources.caloriesPerDay != null) context.metrics.calories_per_day = sources.caloriesPerDay;

  const macros = sources.macronutrients
    ? compact({
        carbs: sources.macronutrients.carbs,
        fat: sources.macronutrients.fat,
        protein: sources.macronutrients.protein,
        fiber: sources.macronutrients.fiber,
      })
    : undefined;
  if (macros) context.metrics.macronutrients = macros;

  const cgm = sources.cgm
    ? compact({
        time_in_range_pct: sources.cgm.timeInRangePct,
        stats: sources.cgm.stats,
      })
    : undefined;
  if (cgm) context.metrics.cgm = cgm;

  return context;
}

/** Short human summary line for the context preview / clipboard header. */
export const patientContextSummary = (ctx: PatientContext): string => {
  const parts: string[] = [];
  const { age, sex } = ctx.core.patient;
  if (age != null) parts.push(sex ? `${age}y ${sex}` : `${age}y`);
  else if (sex) parts.push(sex);
  if (ctx.core.motivations.length) parts.push(`${ctx.core.motivations.length} motivation(s)`);
  if (ctx.core.dietary_restrictions.length)
    parts.push(`${ctx.core.dietary_restrictions.length} restriction(s)`);
  if (ctx.metrics.cgm) parts.push('CGM');
  if (ctx.metrics.diet_quality?.med_score != null) parts.push('diet quality');
  return parts.length ? `Patient context — ${parts.join(' · ')}` : 'Patient context';
};

/**
 * Render the context as the string embedded in the assistant prompt / copied to
 * the clipboard. Wraps the JSON in `<patient_context>` (distinct from the
 * page-specific `<context>` blocks quick-action chips embed).
 */
/**
 * Presentation rule prepended to the context so the assistant answers in plain
 * clinical language instead of echoing the raw JSON field names/paths back to
 * the clinician (e.g. "protein_g", "average_plate.overall.protein",
 * "diet_quality.fat_score"). Keep the examples brace-free — the copy/prompt
 * builders slice the JSON body by the first "{".
 */
export const CONTEXT_PRESENTATION_GUIDANCE =
  'Write every reply in natural clinical language for a dietitian. Never surface raw ' +
  'field names, JSON keys/paths, or variable names from the context below — e.g. say ' +
  '"protein target" not "protein_g", "protein share of the average plate" not ' +
  '"average_plate.overall.protein", and "Mediterranean fat sub-score" not ' +
  '"diet_quality.fat_score". Translate every value into plain words and clinical units.';

export function formatPatientContext(ctx: PatientContext): string {
  const title = `Patient #${ctx.core.patient.id}`;
  return `# ${title}\n${patientContextSummary(ctx)}\n\n${CONTEXT_PRESENTATION_GUIDANCE}\n\n<patient_context>\n${JSON.stringify(
    ctx,
    null,
    2,
  )}\n</patient_context>`;
}
